import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  AccessTime as AccessTimeIcon,
  DragIndicator as DragIndicatorIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Info as InfoIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { getDueDateInfo } from '../../utils/dateUtils';
import { getProgress } from '../../utils/taskUtils';
import { format } from 'date-fns';

const formatDate = (date) => {
  if (!date) return '';
  return format(new Date(date), 'MMM d, yyyy h:mm a');
};

function DraggableTask({ task, index, onEdit, onArchive, isMobile, onMoveUp, onMoveDown }) {
  const getMetadataText = () => {
    const lines = [
      `Created by ${task.createdBy.firstName} ${task.createdBy.lastName}`,
      task.createdAt && `Created on ${formatDate(task.createdAt)}`,
      task.lastModifiedBy && `Last modified by ${task.lastModifiedBy.firstName} ${task.lastModifiedBy.lastName}`,
      task.lastModifiedAt && `Last modified on ${formatDate(task.lastModifiedAt)}`
    ].filter(Boolean);
    
    return lines.join('\n');
  };

  return (
    <Draggable draggableId={task._id} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.5 : 1,
          }}
          sx={{
            p: { xs: 1, sm: 2 },
            mb: 2,
            bgcolor: task.backgroundColor || 'white',
            '&:hover': {
              boxShadow: 2,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                  {onMoveUp && (
                    <IconButton 
                      size="small" 
                      onClick={onMoveUp}
                      sx={{ p: 0.5 }}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                  )}
                  {onMoveDown && (
                    <IconButton 
                      size="small" 
                      onClick={onMoveDown}
                      sx={{ p: 0.5 }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <div {...provided.dragHandleProps}>
                  <DragIndicatorIcon sx={{ color: 'grey.500', cursor: 'grab' }} />
                </div>
              )}
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 'medium', 
                  flex: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {task.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                <IconButton
                  size="small"
                  onClick={() => onArchive(task)}
                  title={task.archived ? "Unarchive" : "Archive"}
                >
                  {task.archived ? 
                    <UnarchiveIcon fontSize="small" /> : 
                    <ArchiveIcon fontSize="small" />
                  }
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onEdit(task)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {task.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                display: '-webkit-box',
                WebkitLineClamp: isMobile ? 2 : 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {task.description}
            </Typography>
          )}

          {task.dueDate && (
            <Box sx={{ mt: 1 }}>
              {(() => {
                const dueInfo = getDueDateInfo(task.dueDate);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon 
                      fontSize="small" 
                      sx={{ color: dueInfo.color }} 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: dueInfo.color,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' }
                      }}
                    >
                      {dueInfo.isOverdue
                        ? `Overdue by ${Math.abs(dueInfo.daysLeft)} days`
                        : dueInfo.daysLeft === 0
                        ? 'Due today'
                        : `Due in ${dueInfo.daysLeft} days`}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}

          {task.checklist?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={getProgress(task)}
                sx={{ height: 4, borderRadius: 1 }}
              />
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  mt: 0.5, 
                  display: 'block',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' }
                }}
              >
                {task.checklist.filter(item => item.completed).length}/{task.checklist.length} items
              </Typography>
            </Box>
          )}

          {!isMobile && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Tooltip 
                title={getMetadataText()} 
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: { 
                      whiteSpace: 'pre-line',
                      maxWidth: 'none'
                    }
                  }
                }}
              >
                <IconButton size="small" sx={{ ml: -1 }}>
                  <InfoIcon fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Paper>
      )}
    </Draggable>
  );
}

export default DraggableTask; 